import { themeVar } from '@/styles/theme';
import styled from 'styled-components';

import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { CloseInfoIcon } from '@/assets/icons/info-icons/closeInfo';
import { TerminatedIcon } from '@/assets/icons/investment-status/treminated';
import Button from '@/components/basicComponents/button';
import { Tooltip } from '@/components/basicComponents/tooltip';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { formatCash } from '@/shared/utils/currency';
import { useMemo } from 'react';
import { AllocatorPageMode, AllocatorTabsEnum, ExtendedBulkAllocation } from './types';

const submitButtonLabels = {
  [AllocatorTabsEnum.BeginAllocation]: 'Submit to Investment Review',
  [AllocatorTabsEnum.InvestmentReview]: 'Submit to Advisor Review',
  [AllocatorTabsEnum.AdvisorReview]: 'Submit to Compliance Review',
  [AllocatorTabsEnum.ComplianceReview]: 'Confirm Allocation Decisions'
};

interface FooterModalProps {
  mode: AllocatorPageMode;
  summary: any;
  selectedRows: ExtendedBulkAllocation[];
  allocationReviewFlag: boolean;
  footerMessages: { greenMessage: string; redMessage: string };
  handleChangeMode: (mode: AllocatorPageMode) => void;
  handleSubmitTo: () => void;
}

const FooterModal = ({ mode, summary, selectedRows, allocationReviewFlag, footerMessages, handleChangeMode, handleSubmitTo }: FooterModalProps) => {
  const { isMobile, isTablet } = useResponsive();
  const { searchParams } = useSearchParamsManager();

  const activeTab = (searchParams.get('activeTab') || AllocatorTabsEnum.BeginAllocation) as AllocatorTabsEnum;

  const totalValue = useMemo(() => {
    return selectedRows
      .filter((selectedRow) => selectedRow.decision && selectedRow.reason !== 'Always Show')
      .reduce((accumulator, currentValue) => accumulator + currentValue.committedCapital, 0);
  }, [selectedRows]);

  const totalYesSelected = useMemo(() => {
    return selectedRows.filter((selectedRow) => selectedRow.decision && selectedRow.reason !== 'Always Show').length;
  }, [selectedRows]);

  return (
    <FooterModalWrap isMobile={isMobile} isTablet={isTablet}>
      {mode === AllocatorPageMode.VIEW ? (
        <>
          <Tooltip title="Your permissions do not allow you to edit any of the allocations shown here." maxContentWidth={320} disableTooltip={true}>
            <Button size="lg" onClick={() => handleChangeMode(AllocatorPageMode.EDIT)} styleType={!summary.count ? 'disabled' : 'default'}>
              Edit
            </Button>
          </Tooltip>

          <FooterDescription>
            You have
            <BackWrap>
              <Count>{summary.count}</Count>
            </BackWrap>
            allocations to review (
            <BoldInfo>
              {summary.yesCount} marked Yes totaling {formatCash(summary.totalAmount || 0)}
            </BoldInfo>
            )
          </FooterDescription>
        </>
      ) : (
        <EditWrap>
          <BtnWrap>
            <Button
              size="lg"
              onClick={handleSubmitTo}
              styleType={!selectedRows.length ? 'disabled' : 'default'}
              icon={
                <BackEditWrap disabled={!selectedRows.length}>
                  <EditCount disabled={!selectedRows.length}>{selectedRows.length}</EditCount>
                </BackEditWrap>
              }
            >
              {allocationReviewFlag ? submitButtonLabels[activeTab] : 'Confirm Allocation'}
            </Button>
            {footerMessages.greenMessage || footerMessages.redMessage ? (
              <InfoMessagesWrapper>
                {footerMessages.greenMessage && (
                  <InfoMessage color={themeVar('context.success')}>
                    <ApprovedIcon fill={themeVar('context.success')} />
                    {footerMessages.greenMessage}
                  </InfoMessage>
                )}
                {footerMessages.redMessage && (
                  <InfoMessage color={themeVar('context.error')}>
                    <TerminatedIcon width={26} height={26} fill={themeVar('context.error')} />
                    {footerMessages.redMessage}
                  </InfoMessage>
                )}
              </InfoMessagesWrapper>
            ) : (
              <EditDesc>
                (
                <BoldInfo>
                  {totalYesSelected} marked Yes totaling {formatCash(totalValue)}
                </BoldInfo>
                )
              </EditDesc>
            )}
          </BtnWrap>

          <ExitMyTask onClick={() => handleChangeMode(AllocatorPageMode.VIEW)}>
            <CloseInfoIcon width={18} height={18} fill={themeVar('action.primary')} />
            Exit My Tasks
          </ExitMyTask>
        </EditWrap>
      )}
    </FooterModalWrap>
  );
};

const InfoMessagesWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const InfoMessage = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 700;
  line-height: 22.4px;
  color: ${({ color }) => color};
`;

const EditDesc = styled.div`
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0;
  text-align: left;
  color: ${themeVar('font.base')};
`;

const BtnWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  @media (max-width: 990px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const Count = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  font-size: 13px;
`;

const EditCount = styled(Count)<{ disabled: boolean }>`
  color: ${({ disabled, theme }) => (disabled ? themeVar('font.disabled') : themeVar('font.strong'))};
`;

const BackWrap = styled.div`
  background-color: ${themeVar('footer.base')};
  display: inline-block;
  border-radius: 12px;
  color: ${themeVar('context.light')};
  padding: 2px 12px;
  font-size: 13px;
  margin: 0 5px;
`;

const BackEditWrap = styled(BackWrap)<{ disabled: boolean }>`
  line-height: 15px;
  background-color: ${({ disabled, theme }) => (disabled ? themeVar('layer.2') : themeVar('layer.1'))};
`;

const ExitMyTask = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 45px;
  border: 1px solid ${themeVar('action.primary')};
  border-radius: 5px;
  font-family: Blinker, serif;
  font-size: 19px;
  font-weight: 600;
  line-height: 27px;
  letter-spacing: 0;
  color: ${themeVar('action.primary')};
  gap: 13px;
`;

const EditWrap = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FooterModalWrap = styled.div<{ isMobile: boolean; isTablet: boolean }>`
  width: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '100%' : 'calc(100% - 80px)')};
  display: flex;
  align-items: center;
  padding: 20px 80px;
  position: fixed;
  bottom: 0;
  clear: both;
  background-color: ${themeVar('layer.1')};
  border-top: 1px solid ${themeVar('border.base')};
  max-height: 90px;
  z-index: 30;
`;

const FooterDescription = styled.div`
  padding-left: 20px;
  font-family: Blinker, serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: ${themeVar('font.base')};
`;

const BoldInfo = styled.div`
  display: inline-block;
  font-weight: 700;
  font-size: 16px;
  color: ${themeVar('font.base')};
`;

export default FooterModal;
